import { Map as ImmutableMap } from 'immutable';
import PropTypes from 'prop-types';
import React, { useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { transactionsSelectors, transactionsTypes } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import StdDialog from 'components/Dialogs/StdDialog';
import Dump from 'components/Dump';
import InvestmentTransactionEditForm from 'components/Forms/InvestmentTransactionEditForm';
import QButton from 'components/QButton';
import useTxnActions from 'data/transactionActions/useTxnActions';
import trackEventTTI from 'utils/trackEventTTI';

export const DIALOG_TYPE_INVESTMENT_TRANSACTIONS_EDIT = 'DIALOG_TYPE_INVESTMENT_TRANSACTIONS_EDIT';

export const openInvestmentTransactionEditDialogAction = (transaction, { props, ...overrides } = {}) =>
  rootUiActions.createDialog(
    rootUiTypes.mkRootUiData({
      id: 'investment-transaction-edit-dialog',
      type: DIALOG_TYPE_INVESTMENT_TRANSACTIONS_EDIT,
      allowNesting: true,
      props: ImmutableMap({ transaction, ...props }),
      ...overrides,
    }),
  );

const InvestmentTransactionEditDialog = React.memo((props) => {
  const { transaction, context, ...otherProps } = props;

  const dispatch = useDispatch();
  const formikRef = useRef();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const onDirty = useCallback((dirty) => setDisableSubmit(!dirty), [setDisableSubmit]);
  const { deleteTransactions } = useTxnActions({ wrapperId: context });

  const closeForm = useCallback(
    (_event, _reason) => {
      dispatch(rootUiActions.removeDialog(props.dialogId));
    },
    [dispatch, props.dialogId],
  );

  return (
    <StdDialog
      sharedcomponentid={'DIALOG_TYPE_INVESTMENT_TRANSACTIONS_EDIT'}
      size='md'
      title={transaction?.id ? 'Transaction detail' : 'Create transaction'}
      {...otherProps}
      onClose={closeForm}
    >
      <Dump obj={transaction} />

      <DialogContent>
        <InvestmentTransactionEditForm
          context={context}
          transaction={transaction}
          formikRef={formikRef}
          onDirty={onDirty}
          onClose={closeForm}
        />
      </DialogContent>

      <DialogActions>
        {transaction?.id ? (
          <QButton
            id='investment-transaction-delete-button'
            variant='delete'
            onClick={() => {
              const payload = transactionsTypes.mkInvestmentTransaction({
                ...transaction,
                isDeleted: true,
              });
              if (assert(payload?.id, 'transaction missing id', { payload, transaction })) {
                const trackingProps = transactionsSelectors.getTrackingProps(undefined, payload);
                trackEventTTI(tracker.events.txnDelete, trackingProps);

                deleteTransactions([payload]);
                dispatch(rootUiActions.removeDialog(props.dialogId));
              }
            }}
          >
            Delete
          </QButton>
        ) : null}

        <QButton
          variant='contained'
          disabled={Boolean(disableSubmit)}
          onClick={() => formikRef.current?.submitForm?.()}
          id='investment-transaction-edit-submit-button'
        >
          {transaction?.id ? 'Update' : 'Create'}
        </QButton>
      </DialogActions>
    </StdDialog>
  );
});

InvestmentTransactionEditDialog.propTypes = {
  transaction: PropTypes.object, // JS object
  dialogId: PropTypes.string,
  context: PropTypes.string,
};

InvestmentTransactionEditDialog.displayName = 'InvestmentTransactionEditDialog';

export default InvestmentTransactionEditDialog;
